import { QueryKey, keepPreviousData, useQuery } from "@tanstack/react-query";

import Query from "../../domain/core/Query";
import {
  SearchAggregate,
  SearchFilter,
  SearchGate,
  SearchInclude,
  SearchInstruction,
  SearchScope,
  SearchSelect,
  SearchSort,
} from "../../domain/models/http/SearchRequest";

type UseCustomPaginateQuery<T> = {
  queryKey: QueryKey;
  request: InstanceType<typeof Query<T>>;
  includes?: SearchInclude[];
  filters?: SearchFilter[];
  instructions?: SearchInstruction[];
  gates?: SearchGate[];
  enabled?: boolean;
  scopes?: SearchScope[];
  sorts?: SearchSort[];
  aggregates?: SearchAggregate[];
  selects?: SearchSelect[];
  limit?: number;
  page?: number;
  refetchOnMount?: boolean;
  refetchInterval?: number | false;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
};

const useCustomPaginateQuery = <T>({
  queryKey,
  request,
  includes,
  filters,
  instructions,
  gates,
  scopes,
  enabled = true,
  aggregates,
  sorts,
  selects,
  limit = 10,
  page = 1,
  refetchOnMount = false,
  refetchInterval = false,
  refetchOnWindowFocus = false,
  staleTime = 0,
}: UseCustomPaginateQuery<T>) => {
  const {
    data,
    isLoading,
    isFetching,
    isSuccess,
    refetch,
    isFetchedAfterMount,
    error,
  } = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      request.search({
        search: {
          page,
          limit,
          ...(includes && includes.length > 0 && { includes }),
          ...(filters && filters.length > 0 && { filters }),
          ...(instructions && instructions.length > 0 && { instructions }),
          ...(gates && gates.length > 0 && { gates }),
          ...(scopes && scopes.length > 0 && { scopes }),
          ...(aggregates && aggregates.length > 0 && { aggregates }),
          ...(selects && selects.length > 0 && { selects }),
          ...(sorts && sorts.length > 0 && { sorts }),
        },
      }),
    enabled,
    placeholderData: keepPreviousData,
    refetchOnMount,
    refetchInterval,
    refetchOnWindowFocus,
    staleTime,
  });
  return {
    data: data?.data ?? [],
    total: data?.total ?? 0,
    lastPage: data?.last_page ?? 0,
    currentPage: data?.current_page ?? 1,
    perPage: data?.per_page ?? limit,
    from: data?.from ?? 0,
    to: data?.to ?? 0,
    isLoading,
    isFetching,
    isSuccess,
    refetch,
    isFetchedAfterMount,
    error,
  };
};

export default useCustomPaginateQuery;
